import { useState } from 'react';
import { useDynamicDrawerContext } from '../../../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../../../context/snackbar.context';
import {
  GetSubmissionsDocument,
  useBulkVoidSubmissionsMutation,
} from '../../../../types';
import {
  ActionTypeDisplayText,
  GridLevelName,
} from '../../../grid-components/grid-types';
import { PanelAlert } from '../../../grid-components/panel-alert';
import { PanelLayout } from '../../../grid-components/panel-layout';
import { PanelTitle } from '../../../grid-components/panel-title';
import { noteInvalid } from '../../../grid-components/panel-validation';
import { SubmissionTypeDataList } from '../../bill-grid-components/bill-management-types';
import { SubmissionPanelProps } from '../panel-types';
import { SelectedSubmissionsGrid } from './selected-submissions-grid';

export const VoidPanel = ({
  selectedRows,
  onClearSelections,
}: SubmissionPanelProps): JSX.Element => {
  const { setIsOpen, setTitle } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();

  const [panelSelectedRows, setPanelSelectedRows] =
    useState<SubmissionTypeDataList>(selectedRows);
  const [note, setNote] = useState<string>('');

  const [bulkVoidSubmissions, { loading }] = useBulkVoidSubmissionsMutation();

  const handleVoid = async (): Promise<void> => {
    try {
      const result = await bulkVoidSubmissions({
        variables: {
          bulkVoidSubmissionsInput: {
            ids: panelSelectedRows
              .map(row => row.submissionId)
              .filter(id => id != null),
            note,
            status: 'void',
          },
        },
        refetchQueries: [GetSubmissionsDocument],
      });
      setSnackbarState(
        `Successfully voided ${result.data?.bulkVoidSubmissions?.length ?? 0} submissions`,
        'success',
        5000,
      );
      onClearSelections();
      setTimeout(() => setIsOpen(false), 500);
    } catch (e) {
      setSnackbarState(
        `Failed to void submissions with error: ${e instanceof Error ? e.message : 'Unknown error'}`,
        'error',
        5000,
      );
    }
  };

  const handleSelectionChanged = (
    selectedGridRows: SubmissionTypeDataList,
  ): void => {
    setPanelSelectedRows(selectedGridRows);
    setTitle(
      <PanelTitle
        actionType={ActionTypeDisplayText.VOID}
        rowCount={selectedGridRows.length}
        gridLevel={GridLevelName.SUBMISSION}
      />,
    );
  };

  return (
    <PanelLayout
      selectedGrid={
        <SelectedSubmissionsGrid
          selectedRows={panelSelectedRows}
          setPanelSelectedRows={handleSelectionChanged}
        />
      }
      alert={<PanelAlert drawerMode="void" />}
      note={note}
      setNote={setNote}
      setIsOpen={setIsOpen}
      isSubmitting={loading}
      isInvalid={noteInvalid(note)}
      actionLabel="Void"
      handleSubmit={handleVoid}
    />
  );
};
